import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import PrivateRoute from 'components/Navigation/ProtectedRoute';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import SolidButton from 'components/SolidButton';
import Table from 'components/Table';

import { formatDate } from 'utils/functions';
import { BUTTON_COLORS } from 'utils/enums';
import { IClaim } from 'utils/interfaces';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import Modal from 'components/Modal';

export default function ClaimsPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [refresh, setRefresh] = useState(true);
  const [loading, setLoading] = useState(false);
  const [claims, setClaims] = useState<Array<IClaim>>([]);

  const [response, setResponse] = useState('');
  const [loadingId, setLoadingId] = useState('');
  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionData, setActionData] = useState<IClaim | undefined>(undefined);
  const [actionLoading, setActionLoading] = useState(false);

  const submitResponse = async () => {
    if (!response) return showErrorToast('Ingrese su respuesta');
    setActionLoading(true);
    let result: any = await generalApi.post('/claim', {
      claimId: actionData?._id,
      answer: response,
    });
    setActionLoading(false);
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('Cambios guardados');
    setIsActionOpen(false);
    setActionData(undefined);
    setRefresh(true);
  };

  const emitPdf = async (claimId: string) => {
    setLoadingId(claimId);
    let result: any = await generalApi.get(`/claim/${claimId}`);
    setLoadingId('');
    if (!result.success) return showErrorToast(result.data.err);
    showSuccessToast('PDF generado');
    window.open(result.data.file_url);
    setRefresh(true);
  };

  useEffect(() => {
    const getClaims = async () => {
      setLoading(true);
      const result = await generalApi.get('/claims');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setClaims(result.data);
      setLoading(false);
    };
    if (refresh) {
      getClaims();
      setRefresh(false);
    }
  }, [generalApi, refresh]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal
          isOpen={isActionOpen}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <div className="rounded-t-lg bg-primary-600 px-4 pb-4 sm:flex sm:items-start sm:p-6 sm:py-4">
            <div className="mt-3 text-center sm:mt-0 sm:text-left">
              <h3 className="text-lg font-medium leading-6 text-white" id="modal-headline">
                Responder reclamo
              </h3>
            </div>
          </div>
          <div>
            <div className="px-4 py-5 sm:p-6">
              {actionData && (
                <>
                  <p className="text-lg font-bold">Cliente</p>
                  <p>{`Nombres: ${actionData.name} ${actionData.lname}`}</p>
                  <p>{`Teléfono: ${actionData.phone || '-'}`}</p>
                  <p className="text-lg font-bold">Detalles del reclamo</p>
                  <p>{`Tipo de reclamo: ${actionData.claim_type === 'Q' ? 'Queja' : 'Reclamo'}`}</p>
                  <p>Descripcion del reclamo:</p>
                  <p>{actionData.claim_description || '-'}</p>
                  <p>Pedido:</p>
                  <p>{actionData.demand || '-'}</p>
                  <p>{`Fecha de operación: ${
                    actionData.operation_date
                      ? formatDate(
                          new Date(actionData.operation_date),
                          'dd/MM/yyyy, hh:mm a',
                          false
                        )
                      : '-'
                  }`}</p>
                  <p>{`Fecha de reclamo: ${
                    actionData.created_at
                      ? formatDate(new Date(actionData.created_at), 'dd/MM/yyyy, hh:mm a', false)
                      : '-'
                  }`}</p>
                </>
              )}
              <div className="relative mt-6 h-[fit-content] rounded-lg border border-gray-400 bg-white px-3 py-2 shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
                <div className="peer">
                  <div className="relative mt-1">
                    <textarea
                      id="response_input"
                      name="response"
                      className="peer block w-full border-0 bg-white p-0 text-gray-900 placeholder-gray-500 focus:ring-0"
                      value={response}
                      onChange={(e) => setResponse(e.target.value)}
                    />
                  </div>
                </div>
                <label
                  htmlFor="inputId"
                  className={`${
                    response !== undefined && (typeof response !== 'string' || response?.length > 0)
                      ? '-top-3 left-3 py-1 text-sm text-gray-900'
                      : 'top-3 left-2 text-gray-400 peer-focus-within:-top-3 peer-focus-within:left-3 peer-focus-within:py-1 peer-focus-within:text-sm peer-focus-within:text-gray-900'
                  } pointer-events-none absolute -mt-px inline-block rounded-md bg-white px-2 font-medium transition-all duration-150`}
                >
                  Respuesta
                </label>
              </div>
            </div>
            <div className="rounded-b-lg bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <SolidButton
                className="w-auto sm:ml-3"
                isLoading={actionLoading}
                onClick={() => submitResponse()}
              >
                Enviar
              </SolidButton>
              <button
                type="button"
                onClick={() => {
                  setIsActionOpen(false);
                  setActionData(undefined);
                }}
                className="focus:ring-primary-500 mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Libro de Reclamaciones</p>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando reclamos"
            initialSort={{
              key: 'created_at',
              order: 'desc',
            }}
            columns={[
              {
                title: 'NOMBRES',
                dataKey: 'name',
              },
              {
                title: 'APELLIDOS',
                dataKey: 'lname',
              },
              {
                title: 'Documento',
                dataKey: 'document_type',
                render: (item: IClaim) => `${item.document_type}: ${item.number}`,
              },
              {
                title: 'Dirección',
                dataKey: 'address',
              },
              {
                title: 'Correo',
                dataKey: 'email',
              },
              {
                title: 'Teléfono',
                dataKey: 'phone',
              },
              {
                title: 'Fecha de la operación',
                dataKey: 'operation_date',
                render: (item: IClaim) =>
                  formatDate(new Date(item.operation_date), 'yyyy/MM/dd, hh:mm a', false),
              },
              {
                title: 'Número de la Operación',
                dataKey: 'operation_number',
              },
              {
                title: 'Descripción de la Operación',
                dataKey: 'operation_description',
              },
              {
                title: 'Tipo de Reclamo',
                dataKey: 'claim_type',
              },
              {
                title: 'Descripción del Reclamo',
                dataKey: 'claim_description',
              },
              {
                title: 'Demanda',
                dataKey: 'demand',
              },
              {
                title: 'Fecha',
                dataKey: 'created_at',
                render: (item: IClaim) =>
                  formatDate(new Date(item.created_at), 'yyyy/MM/dd, hh:mm a', false),
              },
              {
                title: 'OPCIONES',
                dataKey: 'options',
                disableSort: true,
                render: (item: IClaim) => (
                  <div className="flex space-x-2 pr-2">
                    {item.status === '0' && (
                      <SolidButton
                        className="w-auto"
                        color={BUTTON_COLORS.BLUE}
                        onClick={() => {
                          setIsActionOpen(true);
                          setActionData(item);
                        }}
                      >
                        Responder
                      </SolidButton>
                    )}
                    {item.status === '1' && !item.file_url ? (
                      <>
                        <SolidButton
                          className="w-auto"
                          color={BUTTON_COLORS.BLUE}
                          onClick={() => {
                            setIsActionOpen(true);
                            setActionData(item);
                            setResponse(item.answer);
                          }}
                        >
                          Editar
                        </SolidButton>
                        <SolidButton
                          className="w-auto"
                          color={BUTTON_COLORS.BLUE}
                          isLoading={loadingId === item._id}
                          onClick={() => emitPdf(item._id)}
                        >
                          Descargar
                        </SolidButton>
                      </>
                    ) : item.status === '1' ? (
                      <a href={item.file_url} target="_blank" rel="noreferrer">
                        <SolidButton color={BUTTON_COLORS.BLUE}>Descargar</SolidButton>
                      </a>
                    ) : null}
                  </div>
                ),
              },
            ]}
            data={claims}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
